import React, { useState, useEffect } from 'react';
import { UseFormMethods } from 'react-hook-form';
import RHFInputBox from '../../controls/tempRHFControls/RHFInputBox';
import DocumentManagement from 'src/components/layout/DocumentManagement';
import DialogList from 'src/dialogs';
import { useDispatch } from 'react-redux';
import Button from 'src/components/controls/forms/redesign/FormButton';
import actions from 'src/actions';
import { FaDownload, FaTrash } from 'react-icons/fa';

interface IGISPortalFormDetailsProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'control' | 'setValue'>> {
    projectId?: number;
    onFileUploaded?: (formData?: FormData) => void;
    onDownload: (context?: string, index?: number, fileName?: string) => void;
    onDelete: (context: string) => void;
    apiKey?: string;
    mapId?: string;
    isUpdate?: boolean;
    configFileName?: string;
    tractsFileName?: string;
    aoiFileName?: string;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
}

const GISPortalFormDetails: React.FC<IGISPortalFormDetailsProps> = (props) => {
    const dispatch = useDispatch();
    const [filesForm, setFilesForm] = useState<FormData>(undefined);
    const [gisFileType, setGisFileType] = useState<string>(undefined);

    const [tractsFileName, setTractsFileName] = useState<string>();
    const [configFileName, setConfigFileName] = useState<string>();
    const [aoiFileName, setAoiFileName] = useState<string>();
    const [newTractsFileUploaded, setNewTractsFileUploaded] = useState<boolean>(true);
    const [newConfigFileUploaded, setNewConfigFileUploaded] = useState<boolean>(true);
    const [newAoiFileUploaded, setNewAoiFileUploaded] = useState<boolean>(true);

    useEffect(() => {
        resetFilesUploaded();
        setFilesForm(undefined);
    }, [props.projectId]);

    useEffect(() => {
        props.setValue('apiKey', props.apiKey || '');
    }, [props.apiKey]);

    useEffect(() => {
        props.setValue('mapId', props.mapId || '');
    }, [props.mapId]);

    useEffect(() => {
        setConfigFileName(props.configFileName || '');
        if (props.configFileName !== '') {
            setNewConfigFileUploaded(false);
        }
    }, [props.configFileName]);

    useEffect(() => {
        setTractsFileName(props.tractsFileName || '');
        if (props.tractsFileName !== '') {
            setNewTractsFileUploaded(false);
        }
    }, [props.tractsFileName]);

    useEffect(() => {
        setAoiFileName(props.aoiFileName || '');
        if (props.aoiFileName !== '') {
            setNewAoiFileUploaded(false);
        }
    }, [props.aoiFileName]);

    const onFileUploaded = (formData?: FormData) => {
        if (props.onFileUploaded) {
            props.onFileUploaded(formData);

            let name = '';
            let tempName = formData.get('File Name');
            if (typeof tempName === 'string') {
                name = tempName;
            }

            switch (gisFileType) {
                case 'tracts':
                    setTractsFileName(name);
                    setNewTractsFileUploaded(true);
                    break;
                case 'aoi':
                    setAoiFileName(name);
                    setNewAoiFileUploaded(true);
                    break;
                case 'config':
                    setConfigFileName(name);
                    setNewConfigFileUploaded(true);
                    break;
                default:
                    console.log('unknown file type');
                    break;
            }
        } else {
            filesForm.append('Data', 'Data');
        }

        dispatch(actions.layout.hideDialog());
    };

    const resetFilesUploaded = () => {
        setTractsFileName('');
        setAoiFileName('');
        setConfigFileName('');
    };

    const onClick = (context: string) => {
        props.projectId !== null
            ? dispatch(actions.layout.showDialog(DialogList.DOCUMENT_MANAGEMENT))
            : props.showToast('Please select a project before uploading the json files', `rgba(255,0,0,0.5)`);
        setGisFileType(context);
    };

    const downloadFile = (context?: string, index?: number, fileName?: string) => {
        if (props.onDownload) {
            props.onDownload(context, index, fileName);
        }
    };

    const deleteFile = (context: string) => {
        if (props.onDelete) {
            props.onDelete(context);
        }
    };

    return (
        <>
            <DocumentManagement
                filesUploaded={onFileUploaded}
                context="gis map data"
                title="Upload JSON File Here"
                gisFileType={gisFileType}
            />

            <h4>Tracts File</h4>
            <Button
                text="Upload Tracts File"
                onClick={() => onClick('tracts')}
                style={{ marginBottom: '5px', width: '30%' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <p style={{ margin: 0 }}>File uploaded: {tractsFileName}</p>
                {tractsFileName !== '' && !newTractsFileUploaded && (
                    <>
                        <Button
                            text={''}
                            title="Download Tracts File"
                            reactIcon={FaDownload}
                            iconHoverColor="lightblue"
                            iconStyle={{ fontSize: '1.7rem', color: '#4e759a' }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            onClick={() => downloadFile('tracts', 0, props.tractsFileName)}
                        />
                        <a id="a_0" style={{ display: 'none' }} />
                    </>
                )}
                {tractsFileName !== '' && (
                    <Button
                        text={''}
                        title="Delete Tracts File"
                        reactIcon={FaTrash}
                        iconHoverColor="lightblue"
                        iconStyle={{ fontSize: '1.7rem', color: '#4e759a' }}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        onClick={() => deleteFile('tracts')}
                    />
                )}
            </div>
            <br />
            <h4>AOI File</h4>
            <Button
                text="Upload AOI File"
                onClick={() => onClick('aoi')}
                style={{ marginBottom: '5px', width: '30%' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <p style={{ margin: 0 }}>File uploaded: {aoiFileName}</p>
                {aoiFileName !== '' && !newAoiFileUploaded && (
                    <>
                        <Button
                            text={''}
                            title="Download Aoi File"
                            reactIcon={FaDownload}
                            iconHoverColor="lightblue"
                            iconStyle={{ fontSize: '1.7rem', color: '#4e759a' }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            onClick={() => downloadFile('aoi', 1, props.aoiFileName)}
                        />
                        <a id="a_1" style={{ display: 'none' }} />
                    </>
                )}
                {aoiFileName !== '' && (
                    <>
                        <Button
                            text={''}
                            title="Delete Aoi File"
                            reactIcon={FaTrash}
                            iconHoverColor="lightblue"
                            iconStyle={{ fontSize: '1.7rem', color: '#4e759a' }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            onClick={() => deleteFile('aoi')}
                        />
                    </>
                )}
            </div>
            <br />
            <h4>Config File</h4>
            <Button
                text="Upload Config File"
                onClick={() => onClick('config')}
                style={{ marginBottom: '5px', width: '30%' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <p style={{ margin: 0 }}>File uploaded: {configFileName}</p>
                {configFileName !== '' && !newConfigFileUploaded && (
                    <>
                        <Button
                            text={''}
                            title="Download Config File"
                            reactIcon={FaDownload}
                            iconHoverColor="lightblue"
                            iconStyle={{ fontSize: '1.7rem', color: '#4e759a' }}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            onClick={() => downloadFile('config', 2, props.configFileName)}
                        />
                        <a id="a_2" style={{ display: 'none' }} />
                    </>
                )}
                {configFileName !== '' && (
                    <Button
                        text={''}
                        title="Delete Config File"
                        reactIcon={FaTrash}
                        iconHoverColor="lightblue"
                        iconStyle={{ fontSize: '1.7rem', color: '#4e759a' }}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        onClick={() => deleteFile('config')}
                    />
                )}
            </div>

            <br /><hr />
            <RHFInputBox
                id="mapId"
                name="mapId"
                label="Map Id"
                register={props.register}
                disabled={props.projectId ? false : true}
                style={{ width: '100%' }}
            />
            <RHFInputBox
                id="apiKey"
                name="apiKey"
                label="API Key"
                register={props.register}
                disabled={props.projectId ? false : true}
                style={{ width: '100%' }}
            />
        </>
    );
};

export default GISPortalFormDetails;


// WEBPACK FOOTER //
// ./src/components/gis/portal/GISPortalFormDetails.tsx